"use client";

import React from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

export type VerticalSortableProps<T> = {
  items: T[];
  getId: (item: T) => string;
  onReorder: (nextIds: string[]) => void;
  children: (item: T, index: number) => React.ReactNode;
  distance?: number;
  className?: string;
};

export function VerticalSortable<T>({
  items,
  getId,
  onReorder,
  children,
  distance = 4,
  className,
}: VerticalSortableProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance },
    })
  );

  const ids = React.useMemo(() => items.map((it) => getId(it)), [items, getId]);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;
    const oldIndex = ids.indexOf(activeId);
    const newIndex = ids.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(ids, oldIndex, newIndex);
    onReorder(next);
  };

  return (
    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className={className}>
          {items.map((item, i) => children(item, i))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function arrayMove<T>(array: T[], from: number, to: number): T[] {
  const newArr = array.slice();
  const [item] = newArr.splice(from, 1);
  if (item === undefined) return newArr;
  newArr.splice(to, 0, item);
  return newArr;
}

